import { type DxfMappingRole } from '@frontend/api';
import {
  type Point2,
  type ReadableStore,
  type SelectionRef,
  selectionKey,
  selectionOf,
} from '@frontend/viewer-core';

import { BRUSH_HOTKEY_BY_CODE } from './brushHotkeys';
import type { HistoryStore } from './historyStore';
import { selectionEntityKeys } from './mappingModel';
import { isBrushTool, roleOfBrushTool } from './toolController';
import type {
  RoleChromeManager,
  DrawingModel,
  MappingManager,
  MarqueeController,
  MarqueePreview,
  MeasureManager,
  SelectionManager,
  ToolId,
  ToolManager,
  ViewerMode,
  ViewportManager,
} from './types';

/** Wheel delta → zoom factor. One notch ≈ 10%. */
const ZOOM_SENSITIVITY = 0.001;

const PRIMARY_BUTTON = 0;
const MIDDLE_BUTTON = 1;

/**
 * How far the pointer may travel between press and release and still count as a
 * click rather than a drag, in CSS pixels. Generous enough to forgive a shaky
 * hand, tight enough that a deliberate drag is never mistaken for a pick.
 */
const CLICK_SLOP_PX = 3;

type InputControllerOptions = {
  viewport: ViewportManager;
  selection: SelectionManager;
  marquee: MarqueeController;
  /** Live tier: what an in-flight marquee would catch, read at release to paint
   * it — reading this rather than re-deriving the catch keeps the painted set
   * identical to what the preview promised on screen. */
  marqueePreview: ReadableStore<MarqueePreview | null>;
  measure: MeasureManager;
  tool: () => ToolId;
  /** Arms/disarms brushes and switches tools. Driven from the keyboard as well
   * as from toolbar buttons, so both arm a brush the same way. */
  tools: Pick<ToolManager, 'armBrush' | 'set'>;
  /** Where an armed brush's click/marquee paint lands. */
  mapping: Pick<MappingManager, 'assignAndClear'>;
  getModel: () => DrawingModel | null;
  /** The exclusive role chrome — opened here as a context menu, rendered by the mapping menus. */
  roleChrome: Pick<RoleChromeManager, 'open' | 'close' | 'dismiss' | 'release'>;
  mode: () => ViewerMode;
  isPaused: () => boolean;
  /** Undo/redo, driven from the keyboard. Only reachable in mapping mode. */
  history: Pick<HistoryStore, 'undo' | 'redo'>;
  /** Publishes the pointer's position and snap target, or clears it on `null`. */
  setPointer: (screenPoint: Point2 | null) => void;
};

/** True for elements where Space and arrow keys belong to the user, not the viewer. */
const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
};

/**
 * Routes every pointer and key gesture on the render surface.
 *
 * Native listeners rather than React handlers, for two reasons. Wheel has to be
 * non-passive or `preventDefault` is ignored and the page scrolls instead of the
 * drawing zooming — React routes wheel through a passive root listener, so this
 * could never have been a JSX prop. And an earlier React version re-bound its
 * wheel listener on *every pan frame*, because its dependency was a handle object
 * rebuilt each frame; a controller bound once cannot have that bug.
 *
 * Button assignment follows the spec: the primary button belongs to the active
 * tool, and panning is middle-drag or Space-drag. Space and middle-drag pan under
 * every tool, including Measure, so a user is never trapped without a way to move
 * the view.
 *
 * Nothing here touches React state. Gestures write to session stores, and the
 * renderer's subscriptions coalesce them onto one frame.
 */
export const attachInputController = (
  host: HTMLElement,
  {
    viewport,
    selection,
    marquee,
    marqueePreview,
    measure,
    tool,
    tools,
    mapping,
    getModel,
    roleChrome,
    mode,
    isPaused,
    history,
    setPointer,
  }: InputControllerOptions,
): (() => void) => {
  let isPanning = false;
  let isSpaceHeld = false;
  let isAltHeld = false;
  let lastPoint: { x: number; y: number } | null = null;
  let activePointerId: number | null = null;
  /** Where the primary button went down, for telling a click from a drag. */
  let pressOrigin: { x: number; y: number } | null = null;
  /** Whether something pickable sits under the pointer right now. */
  let isOverGeometry = false;
  /** Last cursor written to the surface, so identical writes are skipped. */
  let appliedCursor = '';
  /** True between a press on the surface and its release, whatever it turned into. */
  let isGestureActive = false;

  const surfacePoint = (event: MouseEvent): Point2 => {
    const rect = host.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top];
  };

  /**
   * Whether the current selection already includes the thing `pickAt` named.
   *
   * `pickAt` is loop-granularity, so a drilled edge's key (`Eloop:chunk`) never
   * matches the parent loop key (`Lloop`). Finder-style "right-click keeps the
   * selection" still has to treat that edge as covering its loop — otherwise a
   * right-click on the drilled line expands back to the whole loop.
   */
  const selectionCoversHit = (hit: SelectionRef): boolean => {
    const current = selection.getState();
    if (current.has(selectionKey(hit))) {
      return true;
    }
    if (hit.kind !== 'loop') {
      return false;
    }
    for (const ref of current.values()) {
      if (ref.loopId === hit.loopId) {
        return true;
      }
    }
    return false;
  };

  /** True while any gesture owns the pointer — pan or marquee alike. */
  const isDragging = () => isPanning || marquee.isActive();

  /**
   * The cursor is the viewer's cheapest affordance: it says what the next click
   * will do before the click happens.
   *
   * Ordered by how much each state overrides the last — a drag in progress beats
   * a held modifier, which beats the tool, which beats what happens to be under
   * the pointer. The pointer hand is suppressed mid-marquee because the box, not
   * the geometry beneath it, is what the release will act on.
   */
  const applyCursor = () => {
    const next = isPanning
      ? 'grabbing'
      : isSpaceHeld
      ? 'grab'
      : tool() === 'measure' || isBrushTool(tool())
      ? 'crosshair'
      : isOverGeometry && !marquee.isActive()
      ? 'pointer'
      : '';

    // Compared on the resolved cursor rather than on any one input, so this can
    // be called freely on every move and still writes only on a real change —
    // and so a change nobody told us about, such as the tool switching while the
    // pointer was away over the toolbar, is picked up the moment it returns.
    if (next === appliedCursor) {
      return;
    }
    appliedCursor = next;
    host.style.cursor = next;
  };

  /**
   * Suppresses native text selection for the duration of a gesture, so dragging
   * across the details overlay, the layers panel or a side panel does not leave a
   * trail of highlighted text. Lifted on release rather than toggled per element,
   * so panel text stays selectable whenever no gesture is in progress.
   *
   * The load-bearing mechanism is NOT this function — it is the permanent
   * `user-select: none` on the render surface and on the viewer's floating chrome.
   * Measured in a browser: a press on a `user-select: none` element anchors no
   * selection at all, so the drag cannot select anything however far it travels.
   * What broke before was simply that presses on the layers panel and the toolbar
   * never reached those rules, because both are siblings of the surface rather
   * than children of it.
   *
   * This remains as a backstop for a gesture already under way, and for engines
   * where the style alone might not settle it — `selectstart` covers Blink and
   * WebKit, the scoped style covers Firefox, which does not fire `selectstart`.
   *
   * Scoped to `.viewer-wrapper` (canvas + floating tools + side panels) rather
   * than `document.body`, so other page chrome stays selectable.
   */
  let gestureScope: HTMLElement | null = null;
  let savedUserSelect: string | null = null;
  let savedWebkitUserSelect: string | null = null;

  const setDragHygiene = (isActive: boolean) => {
    isGestureActive = isActive;

    if (isActive) {
      const closestScope = host.closest('.viewer-wrapper');
      const scope = closestScope instanceof HTMLElement ? closestScope : host;
      gestureScope = scope;
      savedUserSelect = scope.style.userSelect;
      savedWebkitUserSelect = scope.style.getPropertyValue(
        '-webkit-user-select',
      );
      scope.style.userSelect = 'none';
      scope.style.setProperty('-webkit-user-select', 'none');
      // A selection begun a moment before the gesture was recognised would otherwise
      // stay highlighted for the whole drag.
      window.getSelection()?.removeAllRanges();
      return;
    }

    if (!gestureScope) {
      return;
    }

    gestureScope.style.userSelect = savedUserSelect ?? '';
    if (savedWebkitUserSelect) {
      gestureScope.style.setProperty(
        '-webkit-user-select',
        savedWebkitUserSelect,
      );
    } else {
      gestureScope.style.removeProperty('-webkit-user-select');
    }
    gestureScope = null;
    savedUserSelect = null;
    savedWebkitUserSelect = null;
  };

  const onSelectStart = (event: Event) => {
    if (isGestureActive) {
      event.preventDefault();
    }
  };

  const releasePointer = () => {
    if (activePointerId !== null && host.hasPointerCapture(activePointerId)) {
      host.releasePointerCapture(activePointerId);
    }
    activePointerId = null;
  };

  const endGesture = () => {
    isPanning = false;
    lastPoint = null;
    pressOrigin = null;
    releasePointer();
    setDragHygiene(false);
    applyCursor();
  };

  /**
   * Applies `role` to every entity behind `loopIds`, resolved through the
   * loop/entity-key bridge — the same one the flyout and toolbar assign
   * through. Painting clears any prior selection in the same write so the
   * flyout does not linger over geometry the brush did not just act on.
   */
  const paintLoops = (loopIds: readonly number[], role: DxfMappingRole) => {
    if (loopIds.length === 0) {
      return;
    }
    const model = getModel();
    if (!model) {
      return;
    }
    const refs = loopIds.map((loopId) => ({ kind: 'loop' as const, loopId }));
    mapping.assignAndClear(selectionEntityKeys(selectionOf(refs), model), role);
  };

  const onWheel = (event: WheelEvent) => {
    if (isPaused()) {
      return;
    }
    // Keeps the page from scrolling under the viewer.
    event.preventDefault();
    const rect = host.getBoundingClientRect();
    viewport.zoomAt(
      [event.clientX - rect.left, event.clientY - rect.top],
      Math.exp(-event.deltaY * ZOOM_SENSITIVITY),
    );
  };

  const onPointerDown = (event: PointerEvent) => {
    if (isPaused()) {
      return;
    }

    // Only presses that land on the surface itself are viewer gestures.
    //
    // Every visual layer over the canvas is `pointer-events: none`, so it never
    // becomes a target and the surface is what a canvas press reports. Real
    // controls sitting over the drawing — the rotate buttons — do become targets,
    // and capturing the pointer for them would steal their click: the Pointer
    // Events spec dispatches the compatibility `click` at the *capture target*,
    // not at the element pressed. Letting those presses through is what makes
    // controls over the canvas work at all.
    if (event.target !== host) {
      return;
    }

    // Any new press on the canvas — including the one that opens a fresh
    // context menu a moment later — dismisses whatever menu a previous
    // right-click left open, without letting the flyout take the slot until
    // this gesture's pointerup (when selection may also clear).
    roleChrome.dismiss();

    const isPanGesture =
      event.button === MIDDLE_BUTTON ||
      (event.button === PRIMARY_BUTTON && isSpaceHeld);

    if (isPanGesture) {
      isPanning = true;
      lastPoint = { x: event.clientX, y: event.clientY };
      activePointerId = event.pointerId;
      host.setPointerCapture(event.pointerId);
      setDragHygiene(true);
      applyCursor();
      // Panning suspends pointer tracking entirely (`onPointerMove` returns
      // before `setPointer` below), so without this the last position from
      // before the pan would stick around — a stale snap target, and a brush
      // cursor badge frozen wherever the pan began.
      setPointer(null);
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    pressOrigin = { x: event.clientX, y: event.clientY };
    activePointerId = event.pointerId;
    host.setPointerCapture(event.pointerId);
    // Applied to every press on the surface, not only the ones that become a
    // marquee: a press that goes on to do nothing can still drag out a selection.
    setDragHygiene(true);

    // Select and every brush box things up — a brush's box paints what it
    // catches instead of selecting it, see `onPointerUp`. Measure takes its
    // points on release, and geometry is inert under it.
    if (tool() === 'select' || isBrushTool(tool())) {
      marquee.begin(surfacePoint(event), {
        isShiftHeld: event.shiftKey,
        isAltHeld: event.altKey,
      });
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (isPanning && lastPoint) {
      viewport.panBy(event.clientX - lastPoint.x, event.clientY - lastPoint.y);
      lastPoint = { x: event.clientX, y: event.clientY };
      return;
    }
    if (isPaused()) {
      return;
    }

    const point = surfacePoint(event);
    setPointer(point);

    if (marquee.isActive()) {
      marquee.move(point);
      return;
    }

    // Hover is a select-tool (and brush) affordance: under Measure, geometry
    // neither highlights nor responds, so nothing suggests it can be acted on.
    const hovered =
      tool() === 'select' || isBrushTool(tool())
        ? selection.pickAt(point)
        : null;
    selection.setHovered(hovered);
    isOverGeometry = hovered !== null;
    applyCursor();
  };

  const onPointerLeave = () => {
    selection.setHovered(null);
    setPointer(null);
    isOverGeometry = false;
    applyCursor();
  };

  const onPointerUp = (event: PointerEvent) => {
    const origin = pressOrigin;
    const wasPanning = isPanning;
    const hadMarquee = marquee.isActive();
    const currentTool = tool();
    const armedRole = isBrushTool(currentTool)
      ? roleOfBrushTool(currentTool)
      : null;

    if (hadMarquee) {
      // Advance to where the pointer actually is before deciding. Pointer moves
      // arrive at a finite rate, so a release that follows the last movement
      // closely would otherwise commit a box one event stale — the other half of
      // "I definitely touched it and it wasn't caught".
      marquee.move(surfacePoint(event));

      if (armedRole) {
        // A brush's box paints what it caught instead of selecting it, and
        // leaves nothing selected either way — so this reads the same catch
        // `commit()` would have used and then discards the gesture rather than
        // running the normal selection write.
        paintLoops(marqueePreview.getState()?.loopIds ?? [], armedRole);
        marquee.abort();
      } else {
        // The controller decides whether the box caught anything; a press that
        // never travelled is left to the click handling below.
        marquee.commit();
      }
    }

    endGesture();

    if (
      wasPanning ||
      !origin ||
      isPaused() ||
      event.button !== PRIMARY_BUTTON ||
      isSpaceHeld
    ) {
      return;
    }

    const travelled = Math.hypot(
      event.clientX - origin.x,
      event.clientY - origin.y,
    );
    if (travelled > CLICK_SLOP_PX) {
      return;
    }

    // The closing click of a double-click belongs to the drill gesture. Acting on
    // it here too would re-select the parent loop a moment before the drill opens
    // its edge, which reads as a flicker.
    if (event.detail >= 2) {
      return;
    }

    const point = surfacePoint(event);

    if (tool() === 'measure') {
      measure.commitPoint(point);
      return;
    }

    if (armedRole) {
      // An empty click paints nothing and leaves no selection either way — the
      // selection path below is bypassed entirely for a brush.
      const hit = selection.pickAt(point);
      if (hit) {
        paintLoops([hit.loopId], armedRole);
      }
      return;
    }

    const hit = selection.pickAt(point);
    if (!hit) {
      // Clicking empty canvas clears, unless the modifier says the user is
      // building up a selection.
      if (!event.shiftKey) {
        selection.clear();
      }
      return;
    }
    if (event.shiftKey) {
      selection.toggle([hit]);
    } else {
      selection.set([hit]);
    }
  };

  /**
   * Double-click drills into the edge under the pointer, and drilling the same
   * edge again comes back out to its loop.
   *
   * A separate `dblclick` listener rather than click-counting on release, because
   * the browser already applies the platform's own timing and movement thresholds
   * for what counts as a double-click — and those differ per OS.
   */
  const onDoubleClick = (event: MouseEvent) => {
    if (isPaused() || event.target !== host || tool() !== 'select') {
      return;
    }
    // Suppresses the browser's own word-selection behaviour on a double press.
    event.preventDefault();
    selection.drillAt(surfacePoint(event));
  };

  const onPointerCancel = () => {
    marquee.abort();
    endGesture();
  };

  /**
   * Right-click never opens the browser's own menu — mid-drag or otherwise —
   * but opens the role menu in its place when there is something for it to
   * act on, in mapping mode.
   *
   * An armed brush puts itself down on the way there rather than taking the
   * click for itself: the spec has right-click disarm a brush, but stopping
   * there would swallow a click that landed on paintable geometry — the same
   * right-click that disarms also selects whatever is under the cursor and
   * opens the menu for it, so disarming never costs a second click.
   *
   * Right-clicking geometry outside the current selection replaces it with
   * just that one thing first — the Finder rule: right-click something
   * already selected and the menu acts on the whole selection, right-click
   * something else and it acts on just what you clicked.
   */
  const onContextMenu = (event: MouseEvent) => {
    event.preventDefault();

    if (isPaused() || mode() !== 'mapping') {
      return;
    }

    if (isBrushTool(tool())) {
      tools.set('select');
    }

    const point = surfacePoint(event);
    const hit = selection.pickAt(point);
    if (hit && !selectionCoversHit(hit)) {
      selection.set([hit]);
    }

    if (selection.getState().size === 0) {
      return;
    }
    roleChrome.open(point);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (isTextEntryTarget(event.target)) {
      return;
    }

    /**
     * Undo/redo, but only while mapping.
     *
     * These listeners are on `window`, not on the surface — a shortcut has to work
     * without the drawing being focused. That makes the mode gate load-bearing
     * rather than cosmetic: without it, the viewer would swallow Cmd-Z for the
     * whole page whenever a DXF happened to be mounted. Nothing else on the
     * session is undoable, so outside mapping mode the keys are left alone.
     *
     * Cmd/Ctrl-Shift-Z and Ctrl-Y are both redo, because Mac and Windows
     * conventions differ and users bring their own.
     */
    const isUndoRedoChord =
      (event.metaKey || event.ctrlKey) &&
      (event.code === 'KeyZ' || event.code === 'KeyY');
    if (isUndoRedoChord) {
      if (isPaused() || mode() !== 'mapping' || isDragging()) {
        return;
      }
      event.preventDefault();
      if (event.code === 'KeyY' || event.shiftKey) {
        history.redo();
      } else {
        history.undo();
      }
      return;
    }

    if (event.code === 'Escape') {
      marquee.abort();
      measure.clear();
      // Selection and an armed brush are mapping-only concepts; view mode's
      // Escape stays scoped to what it already did (abort marquee, clear
      // measure) rather than gaining a new clear-selection side effect.
      if (mode() === 'mapping') {
        selection.clear();
        if (isBrushTool(tool())) {
          tools.set('select');
        }
        roleChrome.close();
      }
      endGesture();
      return;
    }

    // Role brush shortcuts. Mapping-only, and — like the undo/redo chord above
    // — locked out mid-drag so an in-flight gesture always finishes under the
    // rules it began with. Cmd/Ctrl variants are left to the browser — without
    // this gate, Cmd-C would arm the cut brush instead of copying.
    const brushRole = BRUSH_HOTKEY_BY_CODE.get(event.code);
    if (brushRole) {
      if (
        isPaused() ||
        mode() !== 'mapping' ||
        isDragging() ||
        event.metaKey ||
        event.ctrlKey
      ) {
        return;
      }
      event.preventDefault();
      tools.armBrush(brushRole);
      return;
    }

    if (event.code === 'Space' && !event.repeat) {
      // Space would otherwise page-scroll the document behind the viewer.
      event.preventDefault();
      isSpaceHeld = true;
      marquee.setSpace(true);
      applyCursor();
      return;
    }

    if ((event.code === 'AltLeft' || event.code === 'AltRight') && !isAltHeld) {
      // Alt is a browser menu accelerator on some platforms; the viewer needs it.
      event.preventDefault();
      isAltHeld = true;
      marquee.setAlt(true);
    }

    // Everything else is a shortcut, and shortcuts are locked out mid-drag so a
    // gesture always finishes under the rules it began with.
    if (isDragging()) {
      event.preventDefault();
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.code === 'Space') {
      isSpaceHeld = false;
      marquee.setSpace(false);
      applyCursor();
      return;
    }
    if (event.code === 'AltLeft' || event.code === 'AltRight') {
      isAltHeld = false;
      marquee.setAlt(false);
    }
  };

  /** A blurred window never delivers keyup, so a held modifier would latch forever. */
  const onWindowBlur = () => {
    isSpaceHeld = false;
    isAltHeld = false;
    marquee.setSpace(false);
    marquee.setAlt(false);
    marquee.abort();
    endGesture();
    applyCursor();
    roleChrome.release();
  };

  /** Middle-click otherwise opens the browser's autoscroll widget mid-pan. */
  const onAuxClick = (event: MouseEvent) => {
    if (event.button === MIDDLE_BUTTON) {
      event.preventDefault();
    }
  };

  host.addEventListener('wheel', onWheel, { passive: false });
  host.addEventListener('pointerdown', onPointerDown);
  host.addEventListener('pointermove', onPointerMove);
  host.addEventListener('pointerup', onPointerUp);
  host.addEventListener('pointercancel', onPointerCancel);
  host.addEventListener('pointerleave', onPointerLeave);
  host.addEventListener('auxclick', onAuxClick);
  host.addEventListener('dblclick', onDoubleClick);
  host.addEventListener('contextmenu', onContextMenu);
  // Bound on the document, not the surface: the selection a drag would create
  // lives wherever the pointer has reached, which is exactly not the surface.
  document.addEventListener('selectstart', onSelectStart);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onWindowBlur);
  // Window, not host: a context-menu dismiss can land on chrome outside the
  // canvas, and the flyout hold has to end on that pointerup too.
  window.addEventListener('pointerup', roleChrome.release);
  window.addEventListener('pointercancel', roleChrome.release);

  return () => {
    marquee.abort();
    endGesture();
    appliedCursor = '';
    host.style.cursor = '';
    host.removeEventListener('wheel', onWheel);
    host.removeEventListener('pointerdown', onPointerDown);
    host.removeEventListener('pointermove', onPointerMove);
    host.removeEventListener('pointerup', onPointerUp);
    host.removeEventListener('pointercancel', onPointerCancel);
    host.removeEventListener('pointerleave', onPointerLeave);
    host.removeEventListener('auxclick', onAuxClick);
    host.removeEventListener('dblclick', onDoubleClick);
    host.removeEventListener('contextmenu', onContextMenu);
    document.removeEventListener('selectstart', onSelectStart);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onWindowBlur);
    window.removeEventListener('pointerup', roleChrome.release);
    window.removeEventListener('pointercancel', roleChrome.release);
  };
};
